import { Controller } from "@hotwired/stimulus";

/**
 * Keyboard navigation for inbox conversation list
 *
 * Shortcuts:
 *   j/down - Move to next conversation
 *   k/up   - Move to previous conversation
 *   Enter  - Open selected conversation
 *   /      - Focus search input
 *   Escape - Clear selection / blur search
 */
export default class extends Controller {
  static targets = ["list", "item", "link", "searchInput"];

  connect() {
    this.selectedIndex = -1;
  }

  handleKeydown(event) {
    // Don't handle if user is typing in an input (except search)
    if (this.isTypingInInput(event) && event.key !== "Escape") {
      return;
    }

    switch (event.key) {
    case "j":
    case "ArrowDown":
      event.preventDefault();
      this.selectNext();
      break;
    case "k":
    case "ArrowUp":
      event.preventDefault();
      this.selectPrevious();
      break;
    case "Enter":
      event.preventDefault();
      this.openSelected();
      break;
    case "/":
      if (!this.isTypingInInput(event)) {
        event.preventDefault();
        this.focusSearch();
      }
      break;
    case "Escape":
      event.preventDefault();
      this.clearSelection();
      this.blurSearch();
      break;
    }
  }

  selectNext() {
    if (!this.hasItemTarget) return;

    const newIndex = Math.min(this.selectedIndex + 1, this.itemTargets.length - 1);
    this.selectIndex(newIndex);
  }

  selectPrevious() {
    if (!this.hasItemTarget) return;

    const newIndex = Math.max(this.selectedIndex - 1, 0);
    this.selectIndex(newIndex);
  }

  selectIndex(index) {
    // Remove selection from previous item
    if (this.selectedIndex >= 0 && this.itemTargets[this.selectedIndex]) {
      this.itemTargets[this.selectedIndex].classList.remove(
        "bg-indigo-50", "dark:bg-indigo-900/20", "ring-2", "ring-indigo-500"
      );
      this.itemTargets[this.selectedIndex].setAttribute("tabindex", "-1");
    }

    // Select new item
    this.selectedIndex = index;
    const item = this.itemTargets[index];

    if (item) {
      item.classList.add(
        "bg-indigo-50", "dark:bg-indigo-900/20", "ring-2", "ring-indigo-500"
      );
      item.setAttribute("tabindex", "0");
      item.focus();

      // Scroll into view if needed
      item.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }

  openSelected() {
    if (this.selectedIndex >= 0 && this.itemTargets[this.selectedIndex]) {
      const link = this.itemTargets[this.selectedIndex].querySelector("a");
      if (link) {
        link.click();
      }
    }
  }

  clearSelection() {
    if (this.selectedIndex >= 0 && this.itemTargets[this.selectedIndex]) {
      this.itemTargets[this.selectedIndex].classList.remove(
        "bg-indigo-50", "dark:bg-indigo-900/20", "ring-2", "ring-indigo-500"
      );
    }
    this.selectedIndex = -1;
  }

  focusSearch() {
    if (this.hasSearchInputTarget) {
      this.searchInputTarget.focus();
      this.searchInputTarget.select();
    }
  }

  blurSearch() {
    if (this.hasSearchInputTarget && document.activeElement === this.searchInputTarget) {
      this.searchInputTarget.blur();
    }
  }

  isTypingInInput(event) {
    const target = event.target;
    return target.tagName === "INPUT" ||
           target.tagName === "TEXTAREA" ||
           target.isContentEditable;
  }

  // Handle click on conversation item
  itemTargetConnected(element) {
    element.addEventListener("click", () => {
      const index = parseInt(element.dataset.index, 10);
      this.selectIndex(index);
    });
  }
}
